import { callWithAuth, getReviewCards, getCardCount } from "@/api/Api";
import { tokenStorage } from "@/api/tokenStorage";
import { Card } from "@shared_types";

let instance: InfiniteCardStream | null = null;

// ---- implementation ----
export class InfiniteCardStream {
  private cards: Card[] = [];
  private idx: number = 0;

  // tuning knobs
  private readonly maxRetries = 4;
  private readonly baseDelayMs = 500; // first back-off delay (500 ms)

  /**
   * Gets the singleton instance of InfiniteCardStream
   * @returns The singleton instance
   */
  public static getInstance(): InfiniteCardStream {
    if (!instance) {
      instance = new InfiniteCardStream();
    }
    return instance;
  }

  public reset() {
    this.idx = 0;
    this.cards = [];
  }

  // --- public API ----------------------------------------------------------
  // peaks the front of the queue or returns undefined if the queue is empty
  public async peek(): Promise<Card | undefined> {
    if (this.idx >= this.cards.length) return undefined;
    return this.cards[this.idx];
  }

  /**
   * Private constructor to prevent direct instantiation
   * Use InfiniteCardStream.getInstance() instead
   */
  private constructor() {
    if (!tokenStorage.isTokenSet()) {
      return;
    }
  }

  /** Get the next card (waits if necessary). */
  // returns a card, or no card if we hit the card limit or no cards are left to review
  async next(): Promise<Card | undefined> {
    // if queue is empty, block until refill completes
    this.idx++;
    if (this.idx >= this.cards.length) {
      const cards = await this.refill();
      // set the queue without removing the first card
      if (cards.length > 0) {
        this.cards.push(...cards);
        return this.cards[this.idx];
      } else {
        return undefined;
      }
    }
    return this.cards[this.idx];
  }

  // ---- helpers ----
  // tries to refill, if we hit the card limit returns no cards. or returns no cards when none are left to review
  private async refill(): Promise<Card[]> {
    // prevents token forever loop error
    if (!tokenStorage.isTokenSet()) {
      return [];
    }
    try {
      const cardCount = await callWithAuth(getCardCount);
      if (cardCount.count === 0) return [];
    } catch (err) {
      console.error("error getting card count", err);
    }
    try {
      let attempt = 0;
      while (attempt < this.maxRetries) {
        try {
          // prevents token forever loop error
          if (!tokenStorage.isTokenSet()) {
            return [];
          }
          const res = await callWithAuth(getReviewCards);
          const cards = res.cards;
          // if its no more cards and we have previous cards
          // then return/ otherwise try again
          // this is the case that the user has cards, but were failed to fetch
          // for many bad cards self deleting.
          if (cards.length === 0 && this.cards.length > 0) {
            return [];
          }

          return cards; // success
        } catch (err) {
          // dont retry without access
          if ((err as Error).message?.includes("please upgrade")) {
            throw err;
          }
          attempt++;
          if (attempt >= this.maxRetries) throw err;
          const delay = this.baseDelayMs * 2 ** (attempt - 1);
          await new Promise((r) => setTimeout(r, delay));
        }
      }
    } catch (err) {
      throw err;
    }
    return [];
  }
}
